import React from 'react';
import { X, Trash2, Music, ShoppingCart, Tag, Sparkles } from 'lucide-react';
import { useMusicCart } from '../contexts/MusicCartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const MusicCartDrawer = () => {
  const { tracks, isOpen, closeCart, total, regularTotal, savings, 
    bundleApplied, tracksToNextBundle, nextBundlePrice,
    removeTrack, clearCart 
  } = useMusicCart();
  useAuth(); // keeps the cart aware of auth changes (no direct use here)
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Emergency P0 fix (Feb 2026): the old Stripe-backed
  // `/api/payments/checkout/music-cart` endpoint was returning 500 on prod
  // and blocking purchases. Every single-song product already exists in
  // Shopify and is purchasable via the album pages, so the cart now sends
  // buyers to /albums where the Shopify Buy Buttons work reliably.
  const handleCheckout = () => {
    closeCart();
    navigate('/albums');
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50"
        onClick={closeCart}
        data-testid="music-cart-overlay"
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-[#0D0D0D] z-50 flex flex-col border-l border-white/10" data-testid="music-cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Music size={24} className="text-[#D4AF37]" />
            <h2 className="font-['Bebas_Neue'] text-2xl tracking-wider">MUSIC CART</h2>
          </div>
          <button
            onClick={closeCart}
            className="p-2 text-[#A3A3A3] hover:text-white transition-colors"
            data-testid="close-music-cart"
          >
            <X size={24} />
          </button>
        </div>

        {/* Bundle Upsell Banner */}
        {tracksToNextBundle && tracksToNextBundle <= 3 && (
          <div className="mx-6 mt-4 p-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/30">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-[#D4AF37]" />
              <span className="text-sm font-medium text-[#D4AF37]">Bundle Deal!</span>
            </div>
            <p className="text-sm text-white">
              Add <span className="font-bold">{tracksToNextBundle} more track{tracksToNextBundle > 1 ? 's' : ''}</span> to get {
                tracksToNextBundle + tracks.length
              } tracks for just <span className="font-bold text-[#D4AF37]">${nextBundlePrice}</span>
            </p>
          </div>
        )}

        {/* Tracks */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tracks.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="mx-auto text-[#737373] mb-4" />
              <p className="text-[#A3A3A3]">Your music cart is empty</p>
              <p className="text-sm text-[#737373] mt-2">Add tracks to purchase full streaming access</p>
              
              {/* Bundle info for empty cart */}
              <div className="mt-8 text-left bg-[#1A1A1A] p-4 rounded">
                <h4 className="text-sm font-medium text-[#D4AF37] mb-3 flex items-center gap-2">
                  <Tag size={14} />
                  Bundle Deals
                </h4>
                <ul className="space-y-2 text-sm text-[#A3A3A3]">
                  <li>• 3 tracks for <span className="text-white">$2.49</span> <span className="text-green-400">(save $0.48)</span></li>
                  <li>• 5 tracks for <span className="text-white">$3.99</span> <span className="text-green-400">(save $0.96)</span></li>
                  <li>• 10 tracks for <span className="text-white">$6.99</span> <span className="text-green-400">(save $2.91)</span></li>
                </ul>
              </div>
            </div>
          ) : (
            tracks.map((track) => (
              <div key={track.id} className="flex gap-4 pb-4 border-b border-white/10" data-testid={`music-cart-item-${track.id}`}>
                <div className="w-16 h-16 bg-[#1A1A1A] rounded overflow-hidden flex-shrink-0">
                  {track.cover_art_url ? (
                    <img
                      src={track.cover_art_url}
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] flex items-center justify-center">
                      <Music size={24} className="text-[#D4AF37]" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{track.title}</h3>
                  <p className="text-sm text-[#A3A3A3]">{track.genre}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`font-medium ${bundleApplied ? 'text-[#737373] line-through' : 'text-[#D4AF37]'}`}>
                      ${track.price?.toFixed(2) || '0.99'}
                    </span>
                    <button
                      onClick={() => removeTrack(track.id)}
                      className="p-1 text-red-500 hover:text-red-400 transition-colors"
                      data-testid={`remove-track-${track.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {tracks.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-4">
            {/* Bundle Applied Banner */}
            {bundleApplied && (
              <div className="flex items-center justify-between p-3 bg-green-900/30 border border-green-500/30 rounded">
                <div className="flex items-center gap-2">
                  <Tag size={16} className="text-green-400" />
                  <span className="text-sm text-green-400">{bundleApplied}-Track Bundle Applied!</span>
                </div>
                <span className="text-sm text-green-400 font-medium">-${savings.toFixed(2)}</span>
              </div>
            )}

            <div className="flex justify-between items-center">
              <div>
                <span className="text-[#A3A3A3]">{tracks.length} track{tracks.length > 1 ? 's' : ''}</span>
              </div>
              <div className="text-right">
                {bundleApplied && (
                  <span className="text-sm text-[#737373] line-through block">${regularTotal.toFixed(2)}</span>
                )}
                <span className="text-[#D4AF37] font-['Bebas_Neue'] text-3xl" data-testid="music-cart-total">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3 text-xs text-[#D4AF37] mb-2">
                Singles are purchased on the album pages via secure Shopify checkout.
              </div>
              <button
                onClick={handleCheckout}
                className="w-full py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors flex items-center justify-center gap-3"
                data-testid="music-checkout-button"
              >
                Continue on Albums →
              </button>
              <button
                onClick={clearCart}
                className="w-full py-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
                data-testid="clear-music-cart"
              >
                Clear Cart
              </button>
            </div>

            <p className="text-xs text-[#737373] text-center">
              Tap a song&apos;s ADD TO CART on its album page to complete purchase via Shopify.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default MusicCartDrawer;
